import { inject, injectable } from 'inversify';
import ConfigService from '../config/ConfigService';
import { loadScriptWithFallback } from '../helpers/loader';
import { TOKEN } from '../inversify.tokens';
import { TrackLabel } from '../types/common';
import type { AudioTrackProcessorFactory, LoggerFactory } from '../types/container';
import Logger from './Logger';
import { ASDKTrackProcessor } from './media/streamEffects/audio/asdk/ASDKTrackProcessor';
import { compileWASMModule } from './media/streamEffects/audio/noiseSuppression/WasmModuleCompiler';
import { ProcessorsCache } from './media/streamEffects/ProcessorsCache';

export type Integrations = 'asdk' | 'rnnoise'; // later 'esdk' should be added
export interface IntegrationContext {
  integrated: boolean;
  initPromise: Promise<void>;
}

export interface Integration {
  type: Integrations;
  enabled: boolean; // flag to show that integration is requested/enabled through ClientParams
  isInitialized: boolean;
  waitForInitialization: Promise<void>;
}

@injectable()
export class IntegrationsService {
  readonly #integrationsStorage = new Map<Integrations, IntegrationContext>();

  readonly #logger: Logger;

  constructor(
    @inject(TOKEN.Config) private readonly config: ConfigService,
    @inject(TOKEN.ProcessorsCache) private readonly processorsCache: ProcessorsCache,
    @inject(TOKEN.AudioTrackProcessorFactory) private readonly audioTrackProcessorFactory: AudioTrackProcessorFactory,
    @inject(TOKEN.LoggerFactory) loggerFactory: LoggerFactory,
  ) {
    this.#logger = loggerFactory('IntegrationsService');
  }

  init(): void { // must not be async
    const denoiser = this.config.get('denoiser');

    if (denoiser === 'asdk') {
      this.#logger.info('Start integration of ASDK', {
        denoiser,
      });

      const context: IntegrationContext = {
        integrated: false,
        initPromise: this.integrateASDK(),
      };

      this.#integrationsStorage.set('asdk', context);
    } else {
      this.#logger.info('Start integration of RNNoise', {
        denoiser,
      });

      const context: IntegrationContext = {
        integrated: false,
        initPromise: this.integrateRNNoise(),
      };

      this.#integrationsStorage.set('rnnoise', context);
    }
  }

  private async integrateASDK(): Promise<void> {
    const asdkParams = this.config.get('asdk');
    const staticFilesPath = this.config.get('staticFilesPath');

    if (!window.atsvb) {
      const remoteUrl = `https://effectssdk.ai/sdk/audio/dev/${ asdkParams.version }/atsvb-web.js`;
      let localUrl = remoteUrl;

      if (asdkParams.localDir) {
        const localPath = `${ asdkParams.localDir }/${ asdkParams.version }/atsvb-web.js`;
        const url = new URL(localPath, staticFilesPath);
        localUrl = url.href;
      }

      await loadScriptWithFallback(localUrl, remoteUrl);
    }

    if (!window.atsvb) {
      this.#logger.info('Audio Effects SDK is not loaded, skipping initialization', { case: 'initAudioSDK' });

      throw new Error('windows.atsvb is still missing');
    }

    const processor = this.audioTrackProcessorFactory({
      trackLabel: TrackLabel.Microphone,
    }) as ASDKTrackProcessor;

    await processor.init();

    this.processorsCache.add('asdk', TrackLabel.Microphone, processor);

    const context = this.#integrationsStorage.get('asdk');

    if (!context) {
      throw Error('IntegrationContext is missing for ASDK');
    }

    context.integrated = true;
  }

  get audio(): Integration {
    const denoiser = this.config.get('denoiser');

    if (denoiser === 'asdk') {
      return this.getASDKState();
    }

    return this.getRNNoiseState();
  }

  private getASDKState(): Integration {
    const asdk = this.#integrationsStorage.get('asdk');

    return {
      type: 'asdk',
      enabled: Boolean(asdk),
      isInitialized: asdk?.integrated ?? false,
      waitForInitialization: asdk?.initPromise ?? Promise.resolve(),
    };
  }

  private getRNNoiseState(): Integration {
    const rnnoise = this.#integrationsStorage.get('rnnoise');

    return {
      type: 'rnnoise',
      enabled: Boolean(rnnoise),
      isInitialized: rnnoise?.integrated ?? false,
      waitForInitialization: rnnoise?.initPromise ?? Promise.resolve(),
    };
  }

  private async integrateRNNoise(): Promise<void> {
    const noiseSuppressor = await compileWASMModule(this.config.get('staticFilesPath'));

    const processor = this.audioTrackProcessorFactory({
      trackLabel: TrackLabel.Microphone,
      noiseSuppressor,
    });

    this.processorsCache.add('rnnoise', TrackLabel.Microphone, processor);

    const context = this.#integrationsStorage.get('rnnoise');

    if (!context) {
      throw Error('IntegrationContext is missing for RNNoise');
    }

    context.integrated = true;
  }

  reset(): void {
    this.#integrationsStorage.clear();
  }
}
